import type { BodyRect, VisibilitySprite } from './actor-visibility';
export const REVEAL_INNER_ALPHA = 0;
export const REVEAL_FEATHER = 0.24;
export interface HeroRevealEllipse {
  x: number;
  y: number;
  radiusX: number;
  radiusY: number;
}
/** Normalize the actual displayed hero body into its own texture coordinates. */
export function heroRevealEllipse(hero: VisibilitySprite, body: BodyRect): HeroRevealEllipse {
  const { width, height } = hero.texture;
  const sx = hero.scale.x || 1, sy = hero.scale.y || 1;
  const bodyWidth = body.right - body.left, bodyHeight = body.bottom - body.top;
  return {
    x: (((body.left + body.right) / 2 - hero.x) / sx + hero.anchor.x * width) / width,
    y: (((body.top + body.bottom) / 2 - hero.y) / sy + hero.anchor.y * height) / height,
    // The entire body rectangle, including its corners, belongs to the clear core.
    // Otherwise the feather clips different parts of a walking silhouette.
    radiusX: Math.SQRT2 * (bodyWidth / 2 + 4 * Math.abs(sx)) / (1 - REVEAL_FEATHER) / Math.abs(sx) / width,
    radiusY: Math.SQRT2 * (bodyHeight / 2 + 4 * Math.abs(sy)) / (1 - REVEAL_FEATHER) / Math.abs(sy) / height,
  };
}
/** CPU reference for the shader: only the local oval changes occluder opacity. */
export function heroRevealAlpha(x: number, y: number, e: HeroRevealEllipse): number {
  const distance = Math.hypot((x - e.x) / e.radiusX, (y - e.y) / e.radiusY);
  const t = Math.max(0, Math.min(1, (distance - (1 - REVEAL_FEATHER)) / REVEAL_FEATHER));
  return REVEAL_INNER_ALPHA + (1 - REVEAL_INNER_ALPHA) * t * t * (3 - 2 * t);
}

/** Start the effect at the soft oval edge, before the object reaches the hero. */
export function heroRevealBounds(hero: VisibilitySprite, ellipse: HeroRevealEllipse): BodyRect {
  const centerX = hero.x + (ellipse.x - hero.anchor.x) * hero.texture.width * hero.scale.x;
  const centerY = hero.y + (ellipse.y - hero.anchor.y) * hero.texture.height * hero.scale.y;
  const rx = ellipse.radiusX * hero.texture.width * Math.abs(hero.scale.x);
  const ry = ellipse.radiusY * hero.texture.height * Math.abs(hero.scale.y);
  return { left: centerX - rx, top: centerY - ry, right: centerX + rx, bottom: centerY + ry };
}

export interface OwnedRevealTarget { sprite: VisibilitySprite; body: BodyRect }
/** Only the caller's hero and its currently visible camp enter this list. */
export function ownedRevealTargets(hero: VisibilitySprite, heroBody: BodyRect, camp: Iterable<OwnedRevealTarget>, viewport: BodyRect): OwnedRevealTarget[] {
  if (!hero.visible || hero.alpha <= 0 || hero.destroyed) return [];
  const overlap = (a: BodyRect, b: BodyRect) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  return [{ sprite: hero, body: heroBody }, ...camp].filter(({sprite,body}) =>
    sprite.visible && sprite.alpha > 0 && !sprite.destroyed && body.right > body.left && body.bottom > body.top && overlap(body,viewport));
}
export function ownedOccludedTargets(targets: readonly OwnedRevealTarget[], occluder: { zIndex: number; label?: string }, rect: BodyRect): OwnedRevealTarget[] {
  return targets.filter(({sprite,body}) => (occluder.zIndex > sprite.zIndex ||
    (occluder.zIndex === sprite.zIndex && (occluder.label ?? '') > (sprite.label ?? ''))) &&
    body.left < rect.right && body.right > rect.left && body.top < rect.bottom && body.bottom > rect.top);
}

/** Owner-marked camp window: projected pixels at zoom 1, feather stays inside this boundary. */
export function campRevealEllipse(hero: VisibilitySprite): HeroRevealEllipse {
  const sx=hero.scale.x||1,sy=hero.scale.y||1;
  return { x:hero.anchor.x, y:hero.anchor.y-22/sy/hero.texture.height,
    radiusX:90/Math.abs(sx)/hero.texture.width, radiusY:63/Math.abs(sy)/hero.texture.height };
}
/** Only the lower camp inside the marked window participates in object depth decisions. */
export function campRevealTargets(hero:VisibilitySprite,targets:readonly OwnedRevealTarget[]):OwnedRevealTarget[] {
  const bounds=heroRevealBounds(hero,campRevealEllipse(hero));
  return targets.map(target=>({sprite:target.sprite,body:{left:Math.max(target.body.left,bounds.left),
    right:Math.min(target.body.right,bounds.right),top:Math.max(target.body.top,bounds.top),bottom:Math.min(target.body.bottom,bounds.bottom)}}))
    .filter(target=>target.body.left<target.body.right&&target.body.top<target.body.bottom);
}
